import classNames from 'clsx';
import React, { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import type { SetFlowToStep } from '@swf/components/Onboarding/OnboardingContext/OnbordingContext';
import type { RootState } from '@swf/core/store';

import IIcon from '../../assets/icon-i.svg';
import { useTopPrizeDisplay } from '../../hooks/useTopPrizeDisplay';
import { usePokerSpinAndGoWidget } from '../../model';
import type { Game, GameTypeName } from '../../types/Game';

import { TXTCLI_Game_Information } from './Tile.messages.json';
import Background from './components/Background';
import BuyIn from './components/BuyIn';
import TopPrize from './components/TopPrize';
import styles from './shared.scss';

export interface TileProps {
  tile: Game;
  className?: string;
  classNameInner?: string;
  gameType: GameTypeName;
  isActive: boolean;
  widgetId?: string;
  onSelect?: (game: Game) => void;
  onInfoClick?: () => void;
  infoRef?: SetFlowToStep;
  containerRef?: SetFlowToStep;
}

const Tile: React.FC<TileProps> = ({
  tile,
  className,
  classNameInner,
  gameType,
  isActive,
  widgetId,
  onSelect,
  onInfoClick,
  infoRef,
  containerRef
}) => {
  const { widgetSelect } = usePokerSpinAndGoWidget(widgetId);

  const tilesConfig = useSelector(
    (state: RootState) => widgetSelect.getActiveTileConfig(state, gameType),
    shallowEqual
  );
  const tileConfig = useMemo(() => tilesConfig?.[tile.scriptId], [tile, tilesConfig]);
  const { displayCustomPrize, finalPrize } = useTopPrizeDisplay(tile, tileConfig, isActive);

  return (
    <div
      data-testid={tile.scriptId}
      className={classNames(
        styles.tile,
        styles.game,
        {
          [styles.active]: isActive,
          [styles.promo]: tile.isUltra || tile.promoRef,
          [styles.isTileDisabled]: !tile.isEnabled
        },
        className
      )}
      onMouseDown={() => onSelect?.(tile)}
      ref={containerRef}
    >
      {tile.isEnabled && onInfoClick && (
        <i
          className={styles.iconI}
          aria-label="Game info"
          role="button"
          onClick={() => onInfoClick()}
          tabIndex={0}
          onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && onInfoClick()}
          ref={infoRef}
          title={TXTCLI_Game_Information.defaultMessage}
        >
          <IIcon />
        </i>
      )}
      <Background
        winUpTo={tile.winUpTo}
        gameType={gameType}
        isUltra={tile.isUltra}
        url={tile.imageUrl}
        promoRef={tile.promoRef}
        scriptId={tile.scriptId}
        widgetId={widgetId}
      />
      <div className={classNames(styles.inner, classNameInner)}>
        <BuyIn scriptId={tile.scriptId} buyIn={tileConfig?.buyIn || tile.buyIn} />
        <TopPrize
          scriptId={tile.scriptId}
          prize={finalPrize}
          isCustomPrize={displayCustomPrize}
          isUltra={tile.isUltra}
        />
      </div>
    </div>
  );
};
export default Tile;
